<template>
  <template v-if="!isEditAllVersions">
    <div
      :version-index="versionIndex"
      :class="[(isTagged ? 'c-project__version-component-tagged u-mts' : ''), ((versionIndex! > maxVisibleOtherVersions) && !isTagged ? 'c-project__version-component-hidden u-hide' : '')]"
      class="c-form__fields c-project__version-component"
    >
      <div class="c-project__versions-content">
        <div class="c-version__title-container">
          <h3 v-if="version.version_number || dateFormated" class="c-version__title u-mb0">{{ (version.name ? version.name + " - " : "") + version.version_number + " " + dateFormated }}</h3>
          <h3 v-else class="c-version__title u-mb0">{{ version.name }}</h3>
          <button v-if="isTagged && (version.assets.length > maxVisibleAssets)" ref="buttonTaggedVersionAssets" type="button" class="c-version__toggle-button u-mlxs" @click="toggleTaggedVersion">
            <i :class="isTaggedVersionAssetsVisible ? 'fa-chevron-up' : 'fa-chevron-down'" class="c-version__toggle-icon fa-solid" aria-hidden="true" />
          </button>
          <button v-if="!isTagged" ref="buttonOtherVersionAssets" type="button" class="c-version__toggle-button u-mlxs" @click="toggleOtherVersion">
            <i :class="isOtherVersionAssetsVisible ? 'fa-chevron-up' : 'fa-chevron-down'" class="c-version__toggle-icon fa-solid" aria-hidden="true" />
          </button>
        </div>
        <p v-if="version.description" class="c-version__description">
          {{ version.description }}
        </p>
        <p v-if="version.release_notes" class="c-version__notes u-mtxs u-mbxs">
          <ProjectVersionNote
            :version-notes="version.release_notes"
            :version-name="version.name"
          />
        </p>
        <ul ref="assetsListElement" :class="(!isTagged || version.assets.length > maxVisibleAssets)? 'u-hide' : ''" class="c-version__assets">
          <template v-for="asset of sortedAssets" :key="asset.pk">
            <ProjectAsset
              :asset="asset"
              :is-premium-project="isPremiumProject!"
              :is-premium-user="isPremiumUser!"
              :is-vip-user="isVipUser!"
              :is-internal-user="isInternalUser!"
              :is-admin-user="isAdminUser"
              :is-project-owner="isProjectOwner!"
              :is-protected-project="isProtectedProject"
            />
          </template>
        </ul>
        <div
          v-if="deleteVersion.err"
          class="c-message c-message--error u-align-center u-mbs"
        >
          <small>{{ deleteVersion.err }}</small>
        </div>
        <div
          v-if="deleteAsset.err"
          class="c-message c-message--error u-align-center u-mbs"
        >
          <small>{{ deleteAsset.err }}</small>
        </div>
      </div>
    </div>
  </template>
  <template v-if="isEditAllVersions">
    <ProjectVersionForm
      v-if="editVersionForm"
      v-model="editVersionForm"
      :project="project"
      :loading="editVersion.loading"
      :error="editVersion.err"
      :is-edit="true"
      :is-edit-all-versions="isEditAllVersions"
      :is-internal-user="isInternalUser!"
      @add-new-version="addNewVersion"
      @add-new-asset="addNewAsset"
      @select-edit-version="selectEditVersion"
      @select-edit-asset="selectEditAsset"
      @update-versions-order="updateVersionsOrder"
      @update-versions-tagged="updateVersionsTagged"
      @update-assets-order="updateAssetsOrder"
      @canceled="toggleVersionForm"
      @deleted-version="deleteVersion.exec"
      @deleted-asset="deleteAsset.exec"
      @submit-version="editVersion.exec"
      @submit-asset="editAsset.exec"
      @save-asset="saveAsset.exec"
    />
  </template>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import {
  useRequest,
  Project,
  Version,
  VersionList,
  EditVersion,
  getProjectVersion,
  editProjectVersion,
  deleteProjectVersion,
  Asset,
  EditVersionAsset,
  getVersionAsset,
  editVersionAsset,
  editVersionAssetsList,
  deleteVersionAsset
} from '@/utils/api'
// import { useI18n } from '@/utils/i18n'
import ProjectAsset from '@/components/ProjectAsset.vue'
import ProjectVersionForm, { VersionForm, AssetForm } from '@/components/ProjectVersionForm.vue'
import ProjectVersionNote from '@/components/ProjectVersionNote.vue'

const props = withDefaults(defineProps<{
  project: Project
  versionId: number
  versionIndex?: number
  isEditAllVersions?: boolean
  isTagged?: boolean
  isPremiumProject?: boolean
  isPremiumUser?: boolean
  isVipUser?: boolean
  isInternalUser?: boolean
  isAdminUser?: boolean
  isProjectOwner?: boolean
}>(), { versionIndex: undefined, isEditAllVersions: false, isTagged: false, isPremiumProject: false, isPremiumUser: false, isVipUser: false, isInternalUser: false, isProjectOwner: false })

const emit = defineEmits<{
  (e: 'addNewVersion', list: VersionList[]): void
  (e: 'addNewAssetFile'): void
  (e: 'addNewAssetUrl', newAssetForm: AssetForm, version: Version): void
  (e: 'updateVersionsOrder', versionsList: Version[]): void
  (e: 'updateVersionsTagged', versionsList: Version[]): void
  (e: 'updateAssetsOrder', assetsList: Asset[]): void
  (e: 'canceled'): void
  (e: 'updated'): void
  (e: 'deletedVersion', version: Version): void
  (e: 'deletedAsset', asset: Asset): void
}>()

const project = ref(props.project)
// console.log('PROJECT: ', project.value)

const version = ref(await getProjectVersion(props.versionId))
// console.log('VERSION: ', version.value)

const sortedAssets = ref<Asset[]>()
const selectedAsset = ref<Asset>()

if (version.value.assets.length) {
  sortedAssets.value = version.value.assets.slice().sort((a, b) => a.order! - b.order!)

  selectedAsset.value = version.value.assets[0]
} else {
  selectedAsset.value = {
    pk: 0,
    name: '',
    filename: '',
    label: '',
    notes: '',
    platform: '4',
    size: 0,
    order: undefined,
    url: '',
    is_premium: project.value.is_premium ? project.value.is_premium : false
    // authorized_download: true
    // unlock_url: project.value.protection_unlock_url ? project.value.protection_unlock_url : ''
  }
}
// console.log('ASSET: ', selectedAsset.value)

const maxVisibleOtherVersions = 2
const maxVisibleAssets = 5

const isTagged = ref(props.isTagged)
// const isTagged = ref(version.value.is_tagged)
// console.log("VERSION " + version.value.pk + " is tagged : ", isTagged.value)

const isProtectedProject = ref(props.project.is_protected)

const assetsListElement = ref<HTMLUListElement>()
const buttonTaggedVersionAssets = ref<HTMLButtonElement>()
const buttonOtherVersionAssets = ref<HTMLButtonElement>()
const isTaggedVersionAssetsVisible = ref(false)
const isOtherVersionAssetsVisible = ref(false)

function toggleTaggedVersion () {
  isTaggedVersionAssetsVisible.value = !isTaggedVersionAssetsVisible.value

  if (isTaggedVersionAssetsVisible.value) {
    assetsListElement.value?.classList.add('u-show')
    assetsListElement.value?.classList.remove('u-hide')
  } else {
    assetsListElement.value?.classList.add('u-hide')
    assetsListElement.value?.classList.remove('u-show')
  }
}

function toggleOtherVersion () {
  isOtherVersionAssetsVisible.value = !isOtherVersionAssetsVisible.value

  if (isOtherVersionAssetsVisible.value) {
    assetsListElement.value?.classList.add('u-show')
    assetsListElement.value?.classList.remove('u-hide')
  } else {
    assetsListElement.value?.classList.add('u-hide')
    assetsListElement.value?.classList.remove('u-show')
  }
}

const dateFormated = ref('')
if (version.value.date_published) {
  dateFormated.value = version.value.date_published.slice(0, 10).replace('/-/g', '/')
} else {
  dateFormated.value = ''
}

if (version.value.version_number === null) {
  version.value.version_number = ''
}

// add new version
function addNewVersion (list: VersionList[]) {
  emit('addNewVersion', list)
}

// add new asset
const newAssetForm = ref<AssetForm>()

function addNewAsset (f: AssetForm) {
  newAssetForm.value = f

  createNewAsset.exec()
}

const createNewAsset = useRequest(async () => {
  const f = editVersionForm.value!

  // list all version assets pks
  const versionAssets: number[] = []
  for (const index in f.assets) {
    versionAssets.push(f.assets[index].pk)
  }

  // if new asset is file
  // check if new asset has been added to version
  if (version.value.assets.length < versionAssets.length) {
    const body = {
      name: version.value.name,
      description: version.value.description!,
      assets: versionAssets,
      assets_group: version.value.assets[0].asset_group!
    }
    // add new file asset to version AssetGroup
    await editVersionAssetsList(version.value.pk, body)

    // add new file asset metadata
    const assetToEdit = await getVersionAsset(versionAssets[versionAssets.length - 1])

    // if has file
    if (assetToEdit.size > 0) {
      const body = {
        name: newAssetForm.value?.name!,
        label: newAssetForm.value?.label,
        notes: newAssetForm.value?.notes,
        platform: newAssetForm.value?.platform,
        is_premium: newAssetForm.value?.isPremium
        // unlock_url: newAssetForm.value?.unlockUrl
      }

      await editVersionAsset(assetToEdit.pk, body)

      // emit('addNewAssetFile')

      // emit update to rerender
      emit('updated')
    }
  }

  // if new asset is url
  const accessUrlElement = ref(document.querySelector('#asset-upload-link') as HTMLInputElement)
  if (accessUrlElement.value) {
    // if has url
    if (accessUrlElement.value.value.length) {
      // if new asset is url
      // upload asset and set its metadata in ProjectVersions parent component
      emit('addNewAssetUrl', newAssetForm.value!, version.value)
    }
  }
})

// select edit version
async function selectEditVersion (pk: number) {
  // console.log(pk)

  version.value = await getProjectVersion(pk)
  // console.log(version.value)

  if (version.value.assets.length) {
    selectedAsset.value = version.value.assets[0]
  }
  // console.log(selectedAsset.value)

  editVersionForm.value = {
    name: version.value.name,
    description: version.value.description!,
    versionNumber: version.value.version_number!,
    datePublished: version.value.date_published!,
    status: version.value.status!,
    releaseNotes: version.value.release_notes || '',
    assets: version.value.assets,
    assetName: selectedAsset.value?.name,
    assetDescription: selectedAsset.value?.label,
    assetNotes: selectedAsset.value?.notes,
    assetPlatform: selectedAsset.value?.platform,
    assetIsPremium: selectedAsset.value?.is_premium
    // assetUnlockUrl: selectedAsset.value?.unlock_url ?? ''
  }
}

// select edit asset
async function selectEditAsset (pk: number) {
  // console.log(pk)

  selectedAsset.value = await getVersionAsset(pk)
  // console.log(selectedAsset.value)

  editVersionForm.value = {
    name: version.value.name,
    description: version.value.description!,
    versionNumber: version.value.version_number!,
    datePublished: version.value.date_published!,
    status: version.value.status!,
    releaseNotes: version.value.release_notes || '',
    assets: version.value.assets,
    assetName: selectedAsset.value.name,
    assetDescription: selectedAsset.value.label,
    assetNotes: selectedAsset.value.notes,
    assetPlatform: selectedAsset.value.platform,
    assetIsPremium: selectedAsset.value?.is_premium
    // assetUnlockUrl: selectedAsset.value?.unlock_url ?? ''
  }
}

function updateVersionsOrder (versionsList: Version[]) {
  // console.log(versionsList)

  emit('updateVersionsOrder', versionsList as unknown as Version[])
}

function updateVersionsTagged (versionsList: Version[]) {
  // console.log(versionsList)

  emit('updateVersionsTagged', versionsList as unknown as Version[])
}

function updateAssetsOrder (assetsList: Asset[]) {
  // console.log(assetsList)

  emit('updateAssetsOrder', assetsList as unknown as Asset[])
}

// const versionEditOpen = ref(false)

// init edit version form values
const editVersionForm = ref<VersionForm>({
  name: version.value.name!,
  description: version.value.description!,
  versionNumber: version.value.version_number!,
  datePublished: version.value.date_published!,
  status: version.value.status!,
  releaseNotes: version.value.release_notes || '',
  assets: version.value.assets!,
  assetName: selectedAsset.value?.name,
  assetDescription: selectedAsset.value?.label,
  assetNotes: selectedAsset.value?.notes,
  assetPlatform: selectedAsset.value?.platform,
  assetIsPremium: selectedAsset.value?.is_premium!
  // assetUnlockUrl: selectedAsset.value?.unlock_url ?? ''
})

const editVersion = useRequest(async () => {
  // get version reference to check for changes
  const versionRef = ref(await getProjectVersion(props.versionId))

  const f = editVersionForm.value!

  if (!f.datePublished) {
    f.datePublished = new Date().toISOString().split('T')[0]
  } else {
    f.datePublished = f.datePublished.split('T')[0]
  }

  const body = {
    name: f.name,
    description: f.description,
    version_number: f.versionNumber,
    date_published: f.datePublished,
    status: f.status !== versionRef.value.status ? f.status : versionRef.value.status, // prevent trigger of release:published signal in case of input changes
    release_notes: f.releaseNotes
  }
  await editProjectVersion(version.value.pk, body)
  // update displayed version
  for (const key in body) {
    const k = key as keyof EditVersion
    version.value[k] = body[k]!
  }

  // // update project versions
  // project.value = await getProject(props.project.pk)

  // emit update to toggle edit form
  emit('updated')
})

// save asset
const saveAsset = useRequest(async () => {
  const f = editVersionForm.value!

  const body = {
    name: f.assetName!,
    label: f.assetDescription,
    notes: f.assetNotes,
    platform: f.assetPlatform,
    is_premium: f.assetIsPremium
    // unlock_url: f.assetUnlockUrl
  }

  await editVersionAsset(selectedAsset.value?.pk!, body)
  // update displayed version
  for (const key in body) {
    const k = key as keyof EditVersionAsset
    if (selectedAsset.value) {
      if (k !== 'is_premium') {
        selectedAsset.value[k] = body[k]!
      } else {
        selectedAsset.value.is_premium = body[k]!
      }
    }
  }
})

// edit asset
const editAsset = useRequest(async () => {
  const f = editVersionForm.value!

  const body = {
    name: f.assetName!,
    label: f.assetDescription,
    notes: f.assetNotes,
    platform: f.assetPlatform,
    is_premium: f.assetIsPremium
    // unlock_url: f.assetUnlockUrl
  }

  await editVersionAsset(selectedAsset.value?.pk!, body)
  // update displayed version
  for (const key in body) {
    const k = key as keyof EditVersionAsset
    if (selectedAsset.value) {
      if (k !== 'is_premium') {
        selectedAsset.value[k] = body[k]!
      } else {
        selectedAsset.value.is_premium = body[k]!
      }
    }
  }

  // emit update to toggle edit form
  emit('updated')
})

const deleteVersion = useRequest(async () => {
  if (version.value.is_tagged && (project.value.releases.length > 1)) {
    // prevent tagged version deletion
    const deleteVersionTaggedWarningElement = ref(document.querySelector('.delete-version-tagged-warning'))

    if (deleteVersionTaggedWarningElement.value) {
      deleteVersionTaggedWarningElement.value.classList.remove('u-hide')
      deleteVersionTaggedWarningElement.value.classList.remove('u-show')

      // throw new Error('Version is tagged')
    }
  } else {
    // delete version if not tagged
    deleteProjectVersion(version.value.pk)
  }
}, {
  success: () => {
    version.value.is_tagged ? setTimeout(() => { emit('canceled') }, 3000) : emit('deletedVersion', version.value)
  }
  // error: () => { console.log('error'), version.value }
})

const deleteAsset = useRequest(async () => {
  if (version.value.assets.length > 1) {
    deleteVersionAsset(selectedAsset.value?.pk!)
  } else {
    // prevent last asset delete
    const deleteLastAssetWarningElement = ref(document.querySelector('.delete-last-asset-warning'))

    if (deleteLastAssetWarningElement.value) {
      deleteLastAssetWarningElement.value.classList.remove('u-hide')
      deleteLastAssetWarningElement.value.classList.remove('u-show')
    }
  }
}, {
  success: () => {
    version.value.assets.length > 1 ? emit('deletedAsset', selectedAsset.value!) : setTimeout(() => { emit('canceled') }, 3000)
  }
})

// toggle forms
function toggleVersionForm () {
  emit('canceled')
}

// const { t } = useI18n({
//   messages: {
//     en: {
//       edit: 'Edit',
//       delete: 'Delete',
//       releaseNotes: 'Version notes',
//       clickAgainToConfirm: 'Click again to confirm',
//       editAVersion: 'Edit a version'
//     },
//     fr: {
//       edit: 'Modifier',
//       delete: 'Supprimer',
//       releaseNotes: 'Notes de version',
//       clickAgainToConfirm: 'Re-cliquer pour confirmer',
//       editAVersion: 'Modifier la version'
//     }
//   }
// })
</script>

<style scoped>
.actions {
  display: flex;
  gap: 5px;
}

.open-release-notes {
  outline: none;
  border: none;
  background: none;
  text-decoration: underline;
}
.open-release-notes:hover {
  text-decoration: none;
}
</style>
